import React, { useState, useEffect, useCallback } from 'react';
import { supabase, supabaseUrl } from '../lib/supabaseClient';
import { useNavigate } from 'react-router-dom';
import { FaEdit, FaSave, FaTimes, FaMoneyBillWave, FaBed } from 'react-icons/fa';
import AdminSidebar from '../components/AdminSidebar';

const AdminPrices = () => {
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const navigate = useNavigate();

  const checkAuth = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/admin/login');
    }
  }, [navigate]);

  const fetchRoomTypes = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('room_types')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      setRoomTypes(data);
    } catch (error) {
      console.error('Error fetching room types:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
    fetchRoomTypes();
  }, [checkAuth, fetchRoomTypes]);

  const handleEdit = (roomType) => {
    setEditingId(roomType.id);
    setEditPrice(roomType.price_per_night.toString());
  };

  const handleSave = async (id) => {
    try {
      const functionUrl = `${supabaseUrl}/functions/v1/update-room-price`;
      const { apikey, Authorization } = supabase.auth.headers;
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', apikey, Authorization },
        body: JSON.stringify({
          roomTypeId: id,
          newPrice: parseFloat(editPrice)
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error al actualizar el precio: ${response.status} ${errorText}`);
      }

      setEditingId(null);
      setEditPrice('');
      fetchRoomTypes();
    } catch (error) {
      console.error('Error updating room type price:', error);
      alert('Error al guardar el precio. Asegúrate de que es un número válido.');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditPrice('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex">
        <AdminSidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando precios...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar />
      
      <div className="flex-1">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center gap-3">
              <FaMoneyBillWave className="text-green-600" size={32} />
              <div>
                <h1 className="text-3xl font-playfair font-bold text-gray-900">
                  Gestión de Precios
                </h1>
                <p className="text-sm text-gray-600 mt-1">Actualiza los precios por noche de cada tipo de habitación</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <main className="px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {roomTypes.map((roomType) => (
              <div key={roomType.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                {/* Card Header */}
                <div className="bg-gradient-to-r from-green-50 to-blue-50 px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <FaBed className="text-green-600" size={20} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{roomType.name}</h3>
                      <p className="text-xs text-gray-500">Tipo de habitación</p>
                    </div>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-6">
                  {editingId === roomType.id ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Nuevo precio por noche (S/.)
                        </label>
                        <input
                          type="number"
                          value={editPrice}
                          onChange={(e) => setEditPrice(e.target.value)}
                          step="0.01"
                          min="0"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg font-semibold"
                          placeholder="0.00"
                          autoFocus
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSave(roomType.id)}
                          className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium"
                        >
                          <FaSave />
                          Guardar
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="flex-1 flex items-center justify-center gap-2 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors font-medium"
                        >
                          <FaTimes />
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="text-center py-4">
                        <p className="text-sm text-gray-500 mb-2">Precio actual</p>
                        <p className="text-4xl font-bold text-green-600">
                          S/. {parseFloat(roomType.price_per_night).toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">por noche</p>
                      </div>
                      <button
                        onClick={() => handleEdit(roomType)}
                        className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                      >
                        <FaEdit />
                        Editar Precio
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminPrices;
